import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { adminApi } from "@/lib/api";
import { ScheduleCall } from "@/types/admin";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Phone, RefreshCw, ChevronDown, AlertCircle, CheckCheck, XCircle, CalendarClock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { es } from "date-fns/locale";

export function ScheduleCallsTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Queries
  const {
    data: scheduleCalls = [],
    isLoading: isLoadingCalls,
    refetch: refetchCalls
  } = useQuery<ScheduleCall[]>({
    queryKey: ["schedule-calls"],
    queryFn: adminApi.scheduleCalls.getAll
  });

  // Mutations
  const updateCallStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => {
      return adminApi.scheduleCalls.updateStatus(id, status);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["schedule-calls"] });
      toast({
        title: "Estado actualizado",
        description: "El estado de la llamada ha sido actualizado correctamente.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error al actualizar el estado",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  // Handlers
  const handleUpdateCallStatus = async (id: number, status: string) => {
    await updateCallStatusMutation.mutateAsync({ id, status });
  };

  // Formatear fecha
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, "dd/MM/yyyy HH:mm", { locale: es });
    } catch (error) {
      return dateString;
    }
  };

  if (isLoadingCalls) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <RefreshCw className="h-8 w-8 animate-spin text-[#FCAA0D]" />
          <p className="text-zinc-400">Cargando solicitudes de llamadas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6 bg-zinc-900/70 p-4 rounded-xl border border-zinc-800/50 shadow-lg">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-white mb-2 font-montserrat flex items-center">
            <Phone className="h-5 w-5 mr-2 text-[#FCAA0D]" /> Llamadas Programadas
          </h2>
          <p className="text-zinc-400 text-sm">
            Gestiona las solicitudes de llamadas de los clientes • <span className="text-[#FCAA0D]">{scheduleCalls.length}</span> solicitudes
          </p>
        </div>

        <Button
          variant="outline"
          onClick={() => refetchCalls()}
          className="border-zinc-700 text-white hover:bg-zinc-800 w-full sm:w-auto flex items-center"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${isLoadingCalls ? 'animate-spin text-[#FCAA0D]' : ''}`} />
          Actualizar Listado
        </Button>
      </div>

      <Card className="border-zinc-800/50 bg-zinc-900/70 shadow-lg overflow-hidden">
        <CardHeader className="py-4 px-6 border-b border-zinc-800/50 bg-zinc-900/80">
          <CardTitle className="text-white font-medium text-base flex items-center">
            <CalendarClock className="h-5 w-5 mr-2 text-[#FCAA0D]" />
            Solicitudes de Llamada
          </CardTitle>
        </CardHeader>

        <CardContent className="p-4">
          {scheduleCalls.length === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-zinc-800/60">
                <Phone className="h-8 w-8 text-[#FCAA0D]" />
              </div>
              <h3 className="mt-6 text-xl font-semibold text-white">No hay llamadas programadas</h3>
              <p className="mt-3 text-zinc-400 max-w-sm mx-auto">
                Aún no hay solicitudes de llamadas de los clientes.
              </p>
            </div>
          ) : (
            <div className="rounded-md overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-zinc-800/30 transition-colors">
                    <TableHead className="text-zinc-400 font-medium w-[20%]">Nombre</TableHead>
                    <TableHead className="text-zinc-400 font-medium w-[20%]">Contacto</TableHead>
                    <TableHead className="text-zinc-400 font-medium w-[15%]">Fecha de solicitud</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Horario preferido</TableHead>
                    <TableHead className="text-zinc-400 font-medium">Estado</TableHead>
                    <TableHead className="text-right text-zinc-400 font-medium w-[15%]">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduleCalls.map((call) => (
                    <TableRow key={call.id} className="border-zinc-800 group hover:bg-zinc-800/30 transition-colors">
                      <TableCell className="font-medium text-white">
                        <div className="truncate max-w-[200px]">{call.name}</div>
                        <div className="text-xs text-zinc-500 mt-1">
                          ID: {call.id}
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        <div className="flex flex-col">
                          <a href={`mailto:${call.email}`} className="text-[#FCAA0D] hover:underline truncate max-w-[170px]">
                            {call.email}
                          </a>
                          <a href={`tel:${call.phone}`} className="text-zinc-400 hover:text-white">
                            {call.phone}
                          </a>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {formatDate(call.createdAt)}
                      </TableCell>
                      <TableCell className="text-zinc-300">
                        {call.preferredTime ? (
                          <span className="px-2 py-1 text-xs border border-zinc-700 rounded-full bg-zinc-800/60 text-zinc-300 inline-block">
                            {call.preferredTime}
                          </span>
                        ) : (
                          <span className="text-zinc-500 italic text-sm">No especificado</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            call.status === 'completed' ? "bg-green-900/20 text-green-400 hover:bg-green-900/30 border border-green-500/30" :
                            call.status === 'pending' ? "bg-yellow-900/20 text-yellow-400 hover:bg-yellow-900/30 border border-yellow-500/30" :
                            "bg-red-900/20 text-red-400 hover:bg-red-900/30 border border-red-500/30"
                          }
                        >
                          {call.status === 'pending' ? 'Pendiente' :
                            call.status === 'completed' ? 'Completada' :
                            'Cancelada'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 bg-zinc-800/80 border border-zinc-700/50 hover:bg-zinc-700 text-zinc-300 hover:text-white"
                            >
                              Cambiar estado
                              <ChevronDown className="ml-2 h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-700">
                            <DropdownMenuLabel className="text-zinc-400">Cambiar estado</DropdownMenuLabel>
                            <DropdownMenuSeparator className="bg-zinc-800" />
                            <DropdownMenuItem
                              onClick={() => handleUpdateCallStatus(call.id, 'pending')}
                              disabled={call.status === 'pending'}
                              className="text-zinc-300 focus:text-white focus:bg-zinc-800 disabled:text-zinc-500"
                            >
                              <AlertCircle className="mr-2 h-4 w-4 text-yellow-500" />
                              Marcar como pendiente
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateCallStatus(call.id, 'completed')}
                              disabled={call.status === 'completed'}
                              className="text-zinc-300 focus:text-white focus:bg-zinc-800 disabled:text-zinc-500"
                            >
                              <CheckCheck className="mr-2 h-4 w-4 text-green-500" />
                              Marcar como completada
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleUpdateCallStatus(call.id, 'cancelled')}
                              disabled={call.status === 'cancelled'}
                              className="text-zinc-300 focus:text-white focus:bg-zinc-800 disabled:text-zinc-500"
                            >
                              <XCircle className="mr-2 h-4 w-4 text-red-500" />
                              Marcar como cancelada
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>

        <CardFooter className="p-4 border-t border-zinc-800/50 bg-zinc-900/80">
          <div className="w-full text-xs text-zinc-500">
            <span>Las solicitudes de llamada se ordenan por fecha de creación, mostrando primero las más recientes</span>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
}
